import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Divider, Grid } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TaskCreateModal from '../Components/Task/TaskComponents/TaskActions/TaskCreateModal'
import TaskTable from '../Components/Task/TaskComponents/TaskTable'
import SearchInput from '../Components/Common/SearchInput'
import { backgroundColors } from '../Components/Common/Color/Color'
import { Task } from '../redux/task/types'
import { useAppSelector } from '../redux/store'
import useFetchAssignedTaskDetails from '../hooks/taskHooks/useFetchAssignedTaskDetails'
import TaskFilter from '../Components/Task/TaskComponents/TaskFilter'
import { selectUserTeamMemberInfo } from '../redux/auth/selector'

const TaskPage: React.FC = () => {
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [taskData, setTaskData] = useState<Task[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0) // Add this line

  const [filters, setFilters] = useState<{
    Status: string[]
    Importance: string[]
  }>({
    Status: [],
    Importance: [],
  })

  const assignedFilters = useMemo(
    () => ({
      teamMemberEmail: teamMemberDetails.email,
      teamMemberWorkdayId: teamMemberDetails.workdayId,
    }),
    [teamMemberDetails.email, teamMemberDetails.workdayId],
  )

  const { tasks } = useFetchAssignedTaskDetails(assignedFilters, refreshTrigger)

  const sortByDueDate = (a: Task, b: Task) => {
    const dateA = a.dueDate instanceof Date ? a.dueDate.getTime() : 0
    const dateB = b.dueDate instanceof Date ? b.dueDate.getTime() : 0
    return dateA - dateB
  }

  const fetchData = () => {
    const currentDate = new Date()

    const dataWithDaysOfDue = tasks
      .map((task) => {
        const dueDate = task.dueDate ? new Date(task.dueDate) : new Date()
        const timeDiff = dueDate.getTime() - currentDate.getTime()
        const daysOfDue = Math.ceil(timeDiff / (1000 * 3600 * 24))
        return { ...task, dueDate, daysOfDue }
      })
      .filter((task: Task) => !task.isArchived)
      .sort(sortByDueDate)

    setTaskData(dataWithDaysOfDue)
  }

  const handleFilterChange = (selectedFilters: Record<string, string[] | undefined>) => {
    setFilters({
      Status: selectedFilters.Status ?? [],
      Importance: selectedFilters.Importance ?? [],
    })
  }

  const filteredTaskData = taskData
    .filter((data: Task & { startDate?: Date | null; dueDate?: Date | null }) => {
      const statusFilter = filters.Status.length === 0 || filters.Status.includes(data.status ?? '')
      const importanceFilter =
        filters.Importance.length === 0 || filters.Importance.includes(data.importance ?? '')

      return statusFilter && importanceFilter
    })
    .sort(sortByDueDate)

  const searchData = useMemo(
    () =>
      filteredTaskData.filter((value: any) => {
        if (searchQuery.trim() === '') {
          return true
        } else {
          return value.title.toLowerCase().includes(searchQuery.toLowerCase())
        }
      }),
    [filteredTaskData, searchQuery],
  )

  useEffect(() => {
    if (tasks.length > 0) {
      fetchData()
    }
  }, [tasks])

  const handleRefreshData = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  return (
    <Box
      component='div'
      sx={{ p: 0, backgroundColor: backgroundColors.white, width: 'auto' }}
      borderRadius={2}
      boxShadow={1}
    >
      <Grid
        container
        sx={{ p: 2 }}
        flexDirection='row'
        alignItems={'center'}
        justifyContent='space-between'
      >
        <Grid>
          <TaskCreateModal />
        </Grid>
        <Grid>
          <Grid container spacing={2} alignItems='center' display='flex'>
            <Grid>
              <Button
                id='refresh-button'
                color='success'
                startIcon={<RefreshIcon />}
                onClick={handleRefreshData}
                size='small'
              >
                Refresh
              </Button>
            </Grid>
            <Grid>
              <TaskFilter onFilterChange={handleFilterChange} pageName='taskPage' />
            </Grid>
            <Grid>
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <TaskTable data={searchData} refreshTable={Boolean(searchQuery)} />
    </Box>
  )
}

export default TaskPage
